import axios from 'axios';

const API_HOST = process.env.REACT_APP_API_HOST;

export type ApproverType = 'manager' | 'hr_admin' | 'user';

export type ApprovalLevel = {
  approver_type: ApproverType;
  user_id?: string;
  name?: string;
};

export type LeaveWorkflow = {
  id: string;
  name: string;
  levels: ApprovalLevel[];
};

const LEAVE_TYPES = new Set(['timeaway', 'leave', 'time_off', 'time-off']);

function updatedAt(row: any): number {
  return new Date(
    row?.audit?.updated_at ?? row?.audit?.created_at ?? 0,
  ).getTime();
}

/**
 * The company's leave workflow: the most recently updated leave-type
 * workflow, which is the one the API's approval engine uses.
 */
export async function getLeaveWorkflow(): Promise<LeaveWorkflow | null> {
  const { data } = await axios.get(`${API_HOST}/v1/approval`);
  const rows: any[] = Array.isArray(data?.data) ? data.data : [];
  const leaveRows = rows
    .filter((row) =>
      LEAVE_TYPES.has(String(row?.details?.type ?? 'timeaway')),
    )
    .sort((a, b) => updatedAt(b) - updatedAt(a));
  const row = leaveRows[0];
  if (!row?.system?.id) return null;
  return {
    id: String(row.system.id),
    name: row.details?.name || 'Leave approval',
    levels: Array.isArray(row.details?.levels) ? row.details.levels : [],
  };
}

/** Saves the steps, creating the workflow first if the company has none. */
export async function saveLeaveWorkflow(levels: ApprovalLevel[]) {
  let workflow = await getLeaveWorkflow();
  if (!workflow) {
    await axios.post(`${API_HOST}/v1/approval`, {
      name: 'Leave approval',
      type: 'timeaway',
      levels: [],
    });
    workflow = await getLeaveWorkflow();
  }
  if (!workflow) {
    throw new Error('Could not create the approval workflow');
  }
  const { data } = await axios.put(`${API_HOST}/v1/approval/${workflow.id}`, {
    levels,
    active: true,
  });
  return data;
}
